// Pure helpers for the stop, scope-change, cancellation, closeout and archive screens.
// No React and no Supabase; relative imports only, so tests/unit can import this file
// under Node. Nothing here decides whether a step is allowed - the server does that and
// answers with blockers (PW001). These helpers only shape what the person entered into
// command arguments and describe facts the server already recorded.
import { AA_CLOSEOUT_CHECKS, CANCELLATION_REASONS, CYCLE_STATES, PH_CANCELLATION_DECLARATIONS, ROLES, SUSPENSION_REASONS } from '../../domain/catalogue'
import type { RoleCode } from '../../domain/catalogue'
import { VERSIONLESS_COMMANDS } from '../../domain/types'
import type { AvailableAction, CommandArgs, CommandName, CycleView, PermitBundle, PersonRef, SuspensionView } from '../../domain/types'
import { fromLocalInput, labelFor } from '../../lib/format'

export type ToneName = 'neutral' | 'info' | 'ok' | 'warn' | 'bad'

/** A non-blocking note shown next to the inputs; the server still makes the decision. */
export type Hint = { code: string; message: string; rule_ids: string[] }

// -----------------------------------------------------------------------------
// General
// -----------------------------------------------------------------------------

/** Row version to send: none for VERSIONLESS_COMMANDS (stops are never refused as stale). */
export function expectedVersionFor(command: CommandName, bundle: PermitBundle): number | undefined {
  return VERSIONLESS_COMMANDS.includes(command) ? undefined : bundle.permit.row_version
}

/** A non-blank string hint from the server's available action (e.g. cycle_id). */
export function stringArg(action: AvailableAction, key: string): string | null {
  const v = action.args?.[key]
  return typeof v === 'string' && v.trim() !== '' ? v : null
}

export function humanize(code: string | null | undefined): string {
  if (!code) return '-'
  const s = code.replace(/_/g, ' ').trim()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function personText(p: PersonRef | null | undefined): string {
  if (!p) return 'Not recorded'
  return p.organisation ? `${p.display_name} (${p.organisation})` : p.display_name
}

export function roleLabel(role: string | null | undefined): string {
  if (!role) return 'Unknown role'
  return ROLES[role as RoleCode]?.label ?? humanize(role)
}

export function yesNoText(v: boolean | null | undefined): 'Yes' | 'No' | 'Not recorded' {
  return v === true ? 'Yes' : v === false ? 'No' : 'Not recorded'
}

/** Every catalogue code with an explicit true/false, so a signed record never has gaps. */
export function allCodes(options: readonly { code: string }[], values: Record<string, boolean>): Record<string, boolean> {
  return Object.fromEntries(options.map((o) => [o.code, values[o.code] === true]))
}

export function reasonLabel(code: string): string {
  return labelFor(SUSPENSION_REASONS, code)
}

export function cancellationReasonLabel(code: string): string {
  return labelFor(CANCELLATION_REASONS, code)
}

// -----------------------------------------------------------------------------
// Cycles and stops
// -----------------------------------------------------------------------------

export function currentCycle(bundle: PermitBundle): CycleView | null {
  return bundle.cycles.find((c) => c.id === bundle.permit.current_cycle_id) ?? null
}

export function cycleStateText(state: string): { label: string; tone: ToneName } {
  return CYCLE_STATES[state] ?? { label: humanize(state), tone: 'neutral' }
}

const END_REASONS: Record<string, string> = {
  ph_cancellation: 'PH cancellation',
  validation_period_elapsed: 'Validation period over',
}

export function endReasonLabel(reason: string | null | undefined): string | null {
  if (!reason) return null
  return END_REASONS[reason] ?? humanize(reason)
}

export function reporterCapacityLabel(capacity: string): string {
  switch (capacity) {
    case 'PH':
      return 'Permit Holder - formal stop'
    case 'RS':
      return 'Responsible Supervisor - formal stop'
    case 'AA':
      return 'Area Authority (historic) - formal stop'
    case 'fact_reporter':
      return 'Fact report - anyone on site, no licence needed'
    case 'system_gas_result':
      return 'Automatic - unsafe gas test result'
    default:
      return roleLabel(capacity)
  }
}

export type SuspensionConfirmation = SuspensionView['confirmations'][number]

export function phConfirmationOf(s: SuspensionView): SuspensionConfirmation | null {
  return s.confirmations.find((c) => c.confirmation_role === 'PH') ?? null
}

/**
 * The supervisor's confirmation of a stop: the RS's from 0016. An Area Authority confirmation
 * on a record made before 0016 counts the same (its role then reads "Area Authority (historic)").
 */
export function rsConfirmationOf(s: SuspensionView): SuspensionConfirmation | null {
  return s.confirmations.find((c) => c.confirmation_role === 'RS') ?? s.confirmations.find((c) => c.confirmation_role === 'AA') ?? null
}

/**
 * A PH declaration exists when the PH signed the stop report itself (formal stop, with a
 * PH signature) or later signed a PH confirmation. Evidence-based: a signature must exist.
 */
export function hasPhDeclaration(s: SuspensionView): boolean {
  return Boolean(s.ph_attestation) || phConfirmationOf(s) !== null
}

export type SuspensionStage = 'awaiting_ph_and_rs' | 'awaiting_ph' | 'awaiting_rs' | 'confirmed'

export function suspensionStage(s: SuspensionView): SuspensionStage {
  const ph = hasPhDeclaration(s)
  const rs = rsConfirmationOf(s) !== null
  if (ph && rs) return 'confirmed'
  if (ph) return 'awaiting_rs'
  if (rs) return 'awaiting_ph'
  return 'awaiting_ph_and_rs'
}

export const SUSPENSION_STAGE_TEXT: Record<SuspensionStage, { label: string; tone: ToneName }> = {
  awaiting_ph_and_rs: { label: 'Waiting for PH declaration and RS confirmation', tone: 'bad' },
  awaiting_ph: { label: 'Waiting for PH declaration', tone: 'bad' },
  awaiting_rs: { label: 'Waiting for RS confirmation', tone: 'bad' },
  confirmed: { label: 'Confirmed by PH and RS', tone: 'warn' },
}

/** On a cycle that has ended nobody is still waiting: a missing signature is shown as not recorded. */
const SUSPENSION_STAGE_TEXT_ENDED: Record<SuspensionStage, { label: string; tone: ToneName }> = {
  awaiting_ph_and_rs: { label: 'No PH declaration or RS confirmation recorded', tone: 'warn' },
  awaiting_ph: { label: 'No PH declaration recorded', tone: 'warn' },
  awaiting_rs: { label: 'No RS confirmation recorded', tone: 'warn' },
  confirmed: { label: 'Confirmed by PH and RS', tone: 'warn' },
}

/** The stage in words; a stop confirmed by an Area Authority before 0016 says so. */
export function suspensionStageText(s: SuspensionView, cycleState: string): { label: string; tone: ToneName } {
  const stage = suspensionStage(s)
  const text = cycleState === 'ended' ? SUSPENSION_STAGE_TEXT_ENDED[stage] : SUSPENSION_STAGE_TEXT[stage]
  if (stage === 'confirmed' && rsConfirmationOf(s)?.confirmation_role === 'AA') {
    return { ...text, label: `Confirmed by PH and ${roleLabel('AA')}` }
  }
  return text
}

export type CycleStops = { cycle: CycleView; suspensions: SuspensionView[] }

/** Compares instants, not strings, so mixed UTC offsets still order correctly. */
export function newestFirst(a: string, b: string): number {
  const ta = Date.parse(a)
  const tb = Date.parse(b)
  if (Number.isNaN(ta) || Number.isNaN(tb)) return b.localeCompare(a)
  return tb - ta
}

const byNewest = (a: SuspensionView, b: SuspensionView) => newestFirst(a.server_received_at, b.server_received_at)

/** Cycles that have stops, newest cycle first; stops newest first. */
export function stopsByCycle(bundle: PermitBundle): CycleStops[] {
  return bundle.cycles
    .filter((c) => c.suspensions.length > 0)
    .sort((a, b) => b.cycle_number - a.cycle_number)
    .map((cycle) => ({ cycle, suspensions: [...cycle.suspensions].sort(byNewest) }))
}

export type SuspensionChoice = { suspension: SuspensionView; cycle: CycleView }

export function findSuspension(bundle: PermitBundle, id: string | null | undefined): SuspensionChoice | null {
  if (!id) return null
  for (const cycle of bundle.cycles) {
    const suspension = cycle.suspensions.find((s) => s.id === id)
    if (suspension) return { suspension, cycle }
  }
  return null
}

export type ConfirmationRole = CommandArgs['confirm_suspension']['confirmation_role']

/** The role the server offered (action.role): PH or RS; AA only on sandboxes before 0016. */
export function confirmationRoleFor(role: string | null | undefined): ConfirmationRole | null {
  return role === 'PH' || role === 'RS' || role === 'AA' ? role : null
}

/** The confirmation_role the server put in the action's args, else the one its role implies. */
export function confirmationRoleForAction(action: Pick<AvailableAction, 'role' | 'args'>): ConfirmationRole | null {
  const offered = action.args?.confirmation_role
  return confirmationRoleFor(typeof offered === 'string' ? offered : null) ?? confirmationRoleFor(action.role)
}

/**
 * Stops still missing this role's signature: for the PH, no PH declaration yet; for the
 * RS (or a historic AA), no supervisor confirmation yet. Current cycle first, then newest.
 */
export function openSuspensionsFor(bundle: PermitBundle, role: ConfirmationRole): SuspensionChoice[] {
  const out: SuspensionChoice[] = []
  for (const cycle of bundle.cycles) {
    for (const suspension of cycle.suspensions) {
      const open = role === 'PH' ? !hasPhDeclaration(suspension) : rsConfirmationOf(suspension) === null
      if (open) out.push({ suspension, cycle })
    }
  }
  const current = bundle.permit.current_cycle_id
  return out.sort((a, b) => {
    const ca = a.cycle.id === current ? 1 : 0
    const cb = b.cycle.id === current ? 1 : 0
    if (ca !== cb) return cb - ca
    if (a.cycle.cycle_number !== b.cycle.cycle_number) return b.cycle.cycle_number - a.cycle.cycle_number
    return byNewest(a.suspension, b.suspension)
  })
}

/** Choices for the confirmation select: the server's suggested stop first, then the open ones. */
export function suspensionChoices(bundle: PermitBundle, role: ConfirmationRole | null, suggestedId: string | null): SuspensionChoice[] {
  const open = role ? openSuspensionsFor(bundle, role) : []
  const suggested = findSuspension(bundle, suggestedId)
  if (suggested && !open.some((c) => c.suspension.id === suggested.suspension.id)) return [suggested, ...open]
  return open
}

/** Preselect only when unambiguous: the server's suggestion, or the only open stop. */
export function defaultSuspensionId(suggestedId: string | null, choices: SuspensionChoice[]): string {
  if (suggestedId && choices.some((c) => c.suspension.id === suggestedId)) return suggestedId
  return choices.length === 1 ? choices[0].suspension.id : ''
}

// -----------------------------------------------------------------------------
// report_suspension
// -----------------------------------------------------------------------------

export type ReportCapacity = CommandArgs['report_suspension']['capacity']

/**
 * PH and RS report formally in their role (AA only on sandboxes before 0016); everyone else
 * reports a fact (CORE-062/063). Pass the role the server offered (action.role); the panel
 * prefers the capacity the server put in action.args.
 */
export function reportCapacityFor(role: string | null | undefined): ReportCapacity {
  if (role === 'PH' || role === 'RS' || role === 'AA') return role
  return 'fact_reporter'
}

/** The capacity the server put in the action's args (e.g. 'RS'), else the one its role implies. */
export function reportCapacityForAction(action: Pick<AvailableAction, 'role' | 'args'>): ReportCapacity {
  const offered = action.args?.capacity
  if (offered === 'PH' || offered === 'RS' || offered === 'AA' || offered === 'fact_reporter') return offered
  return reportCapacityFor(action.role)
}

export type ReportInput = { reason_code: string; reported_condition: string; observed_local: string }

export function reportUnanswered(i: ReportInput): string[] {
  const out: string[] = []
  if (!i.reason_code) out.push('Choose why the work is stopping')
  if (!i.reported_condition.trim()) out.push('Say what you saw')
  if (!fromLocalInput(i.observed_local)) out.push('Enter when you saw it')
  return out
}

export function buildReportArgs(
  permitId: string,
  cycleId: string | null,
  i: ReportInput,
  capacity: ReportCapacity,
): CommandArgs['report_suspension'] | null {
  const observed = fromLocalInput(i.observed_local)
  if (reportUnanswered(i).length > 0 || !observed) return null
  return {
    permit_id: permitId,
    // No cycle hint: the server applies the stop to the latest cycle (WF-015).
    cycle_id: cycleId,
    reason_code: i.reason_code,
    reported_condition: i.reported_condition.trim(),
    reported_observed_at: observed,
    capacity,
  }
}

// -----------------------------------------------------------------------------
// request_scope_change
// -----------------------------------------------------------------------------

export function buildScopeChangeArgs(permitId: string, description: string, reporterRole: string): CommandArgs['request_scope_change'] | null {
  const text = description.trim()
  if (!text) return null
  return { permit_id: permitId, description: text, reporter_role: reporterRole }
}

// -----------------------------------------------------------------------------
// sign_ph_cancellation
// -----------------------------------------------------------------------------

export type CancellationInput = {
  reason_code: string
  work_complete: boolean | null
  worksite_condition: string
  tools_equipment_removed: boolean | null
  declarations: Record<string, boolean>
  /** GOV-035: for incomplete work, the permit on which it will continue. */
  continuation_reference: string
}

export const EMPTY_CANCELLATION: CancellationInput = {
  reason_code: '',
  work_complete: null,
  worksite_condition: '',
  tools_equipment_removed: null,
  declarations: {},
  continuation_reference: '',
}

/** Choices that have no honest default; everything else is checked by the server. */
export function cancellationUnanswered(i: CancellationInput): string[] {
  const out: string[] = []
  if (!i.reason_code) out.push('Choose the reason for cancelling')
  if (i.work_complete === null) out.push('Answer: is the work complete?')
  if (i.tools_equipment_removed === null) out.push('Answer: are all tools and equipment removed?')
  if (i.work_complete === false && !i.continuation_reference.trim()) {
    out.push('Name the permit on which the incomplete work will continue (or "new permit to be raised")')
  }
  return out
}

/** Mirrors the server's consistency rules as early hints (CORE-070/071). Not a gate. */
export function cancellationHints(i: CancellationInput): Hint[] {
  const out: Hint[] = []
  if (i.work_complete === true && i.reason_code && i.reason_code !== 'work_complete') {
    out.push({
      code: 'COMPLETE_NEEDS_WORK_COMPLETE_REASON',
      message: 'You said the work is complete, so the reason must be "Work complete".',
      rule_ids: ['CORE-070'],
    })
  }
  if (i.work_complete === false && i.reason_code === 'work_complete') {
    out.push({
      code: 'INCOMPLETE_CANNOT_USE_WORK_COMPLETE_REASON',
      message: '"Work complete" can only be the reason when the work is finished. Choose another reason, or answer Yes if it is finished.',
      rule_ids: ['CORE-070'],
    })
  }
  if (i.work_complete === true && i.tools_equipment_removed === false) {
    out.push({
      code: 'COMPLETE_NEEDS_TOOLS_REMOVED',
      message: 'Complete work means all tools and equipment are removed. If anything is left on site, the work is not complete.',
      rule_ids: ['CORE-071'],
    })
  }
  return out
}

export function buildCancellationArgs(permitId: string, i: CancellationInput): CommandArgs['sign_ph_cancellation'] | null {
  if (!i.reason_code || i.work_complete === null || i.tools_equipment_removed === null) return null
  return {
    permit_id: permitId,
    reason_code: i.reason_code,
    work_complete: i.work_complete,
    worksite_condition: i.worksite_condition.trim(),
    tools_equipment_removed: i.tools_equipment_removed,
    declarations: allCodes(PH_CANCELLATION_DECLARATIONS, i.declarations),
    continuation_reference: i.work_complete === false ? i.continuation_reference.trim() || null : null,
  }
}

// -----------------------------------------------------------------------------
// close_out_permit
// -----------------------------------------------------------------------------

export type WorkStatus = CommandArgs['close_out_permit']['work_status_confirmed']

/** What the PH signed: complete iff work_complete (the server compares the RS's closeout answer to this). */
export function phWorkStatus(cancellation: PermitBundle['cancellation']): WorkStatus | null {
  if (!cancellation) return null
  return cancellation.work_complete ? 'complete' : 'incomplete'
}

export type CloseoutInput = {
  checks: Record<string, boolean>
  site_safe_confirmed: boolean
  work_status_confirmed: WorkStatus | null
  inspection_findings: string
  operation_check: string
  /** 0031: what became of the isolation; asked only when the sealed application needed isolation. */
  isolation_disposition: string | null
  isolation_disposition_note: string
}

/** Work status is prefilled from the PH statement; every check starts unticked. */
export function initialCloseoutInput(bundle: PermitBundle): CloseoutInput {
  return {
    checks: {},
    site_safe_confirmed: false,
    work_status_confirmed: phWorkStatus(bundle.cancellation),
    inspection_findings: '',
    operation_check: '',
    isolation_disposition: null,
    isolation_disposition_note: '',
  }
}

/** True when the current revision's sealed application declared that the work needs isolation (0031). */
export function permitNeedsIsolation(bundle: PermitBundle): boolean {
  const rev = bundle.revisions.find((r) => r.id === bundle.permit.current_revision_id) ?? bundle.revisions[bundle.revisions.length - 1]
  return rev?.content?.isolation_required === true
}

/** The isolation summary the sealed application carries (0031), for the closeout and cancellation panels. */
export function permitIsolationSummary(bundle: PermitBundle): { kinds: string[]; reason: string; certificate_reference: string | null } | null {
  if (!permitNeedsIsolation(bundle)) return null
  const rev = bundle.revisions.find((r) => r.id === bundle.permit.current_revision_id) ?? bundle.revisions[bundle.revisions.length - 1]
  const iso = rev?.content?.isolation
  const x = typeof iso === 'object' && iso !== null ? (iso as Record<string, unknown>) : {}
  return {
    kinds: Array.isArray(x.kinds) ? x.kinds.filter((k): k is string => typeof k === 'string') : [],
    reason: typeof x.reason === 'string' ? x.reason : '',
    certificate_reference: typeof x.certificate_reference === 'string' ? x.certificate_reference : null,
  }
}

/** Dispositions that need the note: the permit the isolation moved to, or the EPI reference. */
export function dispositionNeedsNote(code: string | null): boolean {
  return code === 'transferred' || code === 'extended_period_isolation'
}

export function closeoutUnanswered(i: CloseoutInput, needsIsolation = false): string[] {
  const out: string[] = []
  if (i.work_status_confirmed === null) out.push('Choose: is the work complete or incomplete?')
  if (needsIsolation && !i.isolation_disposition) out.push('Record what became of the isolation this permit needed.')
  if (needsIsolation && dispositionNeedsNote(i.isolation_disposition) && !i.isolation_disposition_note.trim()) {
    out.push(i.isolation_disposition === 'transferred' ? 'Name the permit the isolation was transferred to.' : 'Give the EPI reference (F09).')
  }
  return out
}

export function closeoutHints(i: CloseoutInput, phStatus: WorkStatus | null): Hint[] {
  if (!phStatus || !i.work_status_confirmed || i.work_status_confirmed === phStatus) return []
  return [
    {
      code: 'WORK_STATUS_MISMATCH',
      message: `The Permit Holder signed the work as ${phStatus}. Your answer must match what the PH signed. If the site shows something different, do not close out: the permit stays cancelled and work stays stopped. Raise it with the PH and the Responsible Supervisor.`,
      rule_ids: ['CORE-072'],
    },
  ]
}

export function buildCloseoutArgs(permitId: string, i: CloseoutInput, needsIsolation = false): CommandArgs['close_out_permit'] | null {
  if (!i.work_status_confirmed) return null
  return {
    permit_id: permitId,
    site_safe_confirmed: i.site_safe_confirmed,
    work_status_confirmed: i.work_status_confirmed,
    inspection_findings: i.inspection_findings.trim(),
    operation_check: i.operation_check.trim() || null,
    checks: allCodes(AA_CLOSEOUT_CHECKS, i.checks),
    // 0031: sent only for a permit that needed isolation; the server refuses it otherwise.
    ...(needsIsolation && i.isolation_disposition
      ? { isolation_disposition: i.isolation_disposition, isolation_disposition_note: i.isolation_disposition_note.trim() || null }
      : {}),
  }
}

// -----------------------------------------------------------------------------
// Archive
// -----------------------------------------------------------------------------

/** Storage bucket the archive worker writes to; download links are only made for it. */
export const ARCHIVE_BUCKET = 'ptw-archive'
/** Signed download links last five minutes. */
export const SIGNED_URL_SECONDS = 300

const ARCHIVE_STATUS: Record<string, { label: string; tone: ToneName; detail: string }> = {
  queued: { label: 'Queued', tone: 'info', detail: 'Waiting for the archive worker.' },
  processing: { label: 'Processing', tone: 'info', detail: 'The archive worker is making the archive copy now.' },
  retry_pending: {
    label: 'Retry pending',
    tone: 'warn',
    detail: 'The last attempt failed. It will be tried again. The permit stays closed out.',
  },
  succeeded: { label: 'Archived', tone: 'ok', detail: 'The archive copy is stored with its checksums.' },
}

export function archiveStatusText(status: string): { label: string; tone: ToneName; detail: string } {
  return ARCHIVE_STATUS[status] ?? { label: humanize(status), tone: 'neutral', detail: 'Status reported by the server.' }
}

/** The worker can be asked to run only for jobs waiting in the queue. */
export function canProcessArchiveNow(status: string | null | undefined): boolean {
  return status === 'queued' || status === 'retry_pending'
}

/** Retention is never shown as decided unless the server says so (DEC-GOV-007). */
export function retentionText(status: string): { label: string; decision_ids: string[]; tone: ToneName } {
  if (status === 'unresolved') {
    return { label: 'Retention period not yet decided - DEC-GOV-007', decision_ids: ['DEC-GOV-007'], tone: 'warn' }
  }
  if (status === 'assigned') return { label: 'Retention period assigned', decision_ids: [], tone: 'neutral' }
  return { label: humanize(status), decision_ids: [], tone: 'neutral' }
}

const ARTIFACT_KINDS: Record<string, string> = {
  bundle_json: 'Record bundle (JSON)',
  permit_html: 'Printable permit (HTML)',
}

export function artifactKindLabel(kind: string): string {
  return ARTIFACT_KINDS[kind] ?? humanize(kind)
}

/** Exact byte count first (it is evidence), then a readable size. */
export function fmtBytes(n: number | string | null | undefined): string {
  const v = typeof n === 'string' ? Number(n) : n
  if (v === null || v === undefined || !Number.isFinite(v) || v < 0) return '-'
  const exact = `${Math.round(v).toLocaleString('en-GB')} ${v === 1 ? 'byte' : 'bytes'}`
  if (v < 1024) return exact
  const units = ['KB', 'MB', 'GB', 'TB']
  let size = v / 1024
  let i = 0
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024
    i++
  }
  return `${exact} (${size.toFixed(1)} ${units[i]})`
}
